import { Component, Listen, Prop, State, h } from '@stencil/core';

import { Space } from '@artask/tambr-models/models/spaces.model';
import { schedule } from '../../store/schedule.store';



@Component({
  tag: 'tambr-calendar-card-view',
  styleUrl: 'tambr-calendar-card-view.css',
  shadow: true,
})
export class TambrCalendarCardView {

  @Prop() selectedSpaces: string[];

  @State() showCardLeftScroll: boolean = false;
  @State() showCardRightScroll: boolean = false;

  cardSlotsRef: HTMLElement[] = [];
  
  buttonScrolling: boolean = false;
  savedScrollLeft: number = 0;

  @Listen( 'resize', { target: 'window' } ) handleResize() {
    this.updateCardButtonVisibility();
  }

  componentDidLoad() {
    for ( const ref of this.cardSlotsRef ) {
      this.addScrollListener( ref );
    }
  }

  componentDidRender() {
    if ( this.savedScrollLeft > 0 ) {
      for ( const ref of this.cardSlotsRef ) {
        ref.scrollLeft = this.savedScrollLeft;
      }
    }
    this.updateCardButtonVisibility();
  }

  disconnectedCallback() {
    for ( const ref of this.cardSlotsRef ) {
      this.removeScrollListener( ref );
    }
  }

  addScrollListener( ref: HTMLElement ) {
    ref.onscroll = this.handleScroll.bind( this );
  }

  removeScrollListener( ref: HTMLElement ) {
    ref.onscroll = null;
  }


  updateCardButtonVisibility() {
    if( !this.cardSlotsRef?.length ) return;
    const ref = this.cardSlotsRef.find( ref => ref.scrollWidth - ref.offsetHeight > 0 );
    if ( !ref ) return;
    this.showCardRightScroll = ref.scrollLeft <= (ref.scrollWidth - ref.offsetWidth - 5);
    this.showCardLeftScroll = ref.scrollLeft > 5;
    this.savedScrollLeft = ref.scrollLeft;
  }

  handleScroll(event: Event) {
    const el = event.target as HTMLElement;
    requestAnimationFrame( () => {
      this.scrollAll( el );
      this.updateCardButtonVisibility();
    });
  }

  scrollAll( el: HTMLElement ) {
    if ( this.buttonScrolling ) return;
    for ( const ref of this.cardSlotsRef ) {
      if ( ref !== el ) {
        this.removeScrollListener(ref);
        ref.scrollLeft = el.scrollLeft;
        requestAnimationFrame( () => {
          this.addScrollListener( ref );
        });
      } 
    }
  }

  async scrollCardSlots( direction: 'left' | 'right' ) {
    if ( this.buttonScrolling ) return;
    this.buttonScrolling = true;
    const promises = this.cardSlotsRef.map( ref => {
      const operation = direction === 'right' ? 1 : -1;
      const target = ref.scrollLeft + ref.offsetWidth * 0.8 * operation;
      return this.smoothScrollTo( ref, target, 600 );
    } );
    await Promise.all( promises );
    this.buttonScrolling = false;
    this.updateCardButtonVisibility();
  }

  smoothScrollTo( element: HTMLElement, target: number, duration: number ): Promise<void> {
    const start = element.scrollLeft;
    const change = target - start;
    const startTime = performance.now();
    const easeInOutSine = ( t: number, b: number, c: number, d: number ) => {
      return -c / 2 * ( Math.cos( Math.PI * t / d ) - 1 ) + b;
    };

    return new Promise<void>( resolve => {
      const animateScroll = (currentTime: number) => {
        const time = currentTime - startTime;
        element.scrollLeft = easeInOutSine( time, start, change, duration );
        if ( time < duration ) {
          requestAnimationFrame( animateScroll );
        } else {
          resolve();
        }
      };
      requestAnimationFrame( animateScroll );
    });
  }


  render() {
    return (
      <div class='calendar-cards'>
        { this.renderSpaceCards() } 
      </div>
    );
  }

  renderSpaceCards() {
    if ( !schedule.dateSchedule.spaces.length ) return <div>No available spaces</div>
    return schedule.dateSchedule.spaces.map( ( space: Space, index: number ) => 
      <tambr-calendar-card-view-card 
        key={ space.id }
        class='calendar-card'
        hidden={ !this.selectedSpaces.includes(space.id) }
        space={ space } 
        showCardLeftScroll={ this.showCardLeftScroll }
        showCardRightScroll={ this.showCardRightScroll }
        scrollCardSlots={ this.scrollCardSlots.bind(this) }
        onInitCardSlotsRef={ (ev: CustomEvent) => { this.cardSlotsRef[index] = ev.detail } } /> 
      );
  }

}